import { useState, useEffect } from 'react';
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { apiClient } from '../../lib/api-client';
import { Mail, CheckCircle2, XCircle, ChevronLeft, RefreshCw, Calendar } from 'lucide-react';
import { Skeleton } from '../ui/Skeleton';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/Badge';

interface SentEmailSummary {
  id: string;
  to: string[];
  subject: string;
  sentAt: string;
  success: boolean;
  type: string;
}

interface SentEmailRecord {
  id: string;
  to: string[];
  from: string;
  subject: string;
  text?: string;
  html?: string;
  sentAt: string;
  success: boolean;
  messageId?: string;
  error?: string;
  type: string;
  metadata?: Record<string, any>;
}

interface MonthOption {
  year: number;
  month: number;
  label: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function SentEmailsDialog({ open, onClose }: Props) {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emails, setEmails] = useState<SentEmailSummary[]>([]);
  const [months, setMonths] = useState<MonthOption[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<MonthOption | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<SentEmailRecord | null>(null);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) {
      loadAvailableMonths();
    }
  }, [open]);

  useEffect(() => {
    if (selectedMonth) {
      loadEmails(selectedMonth.year, selectedMonth.month);
    }
  }, [selectedMonth]);

  const loadAvailableMonths = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.getSentEmailsMonths();
      const availableMonths = res.months || [];
      setMonths(availableMonths);

      // Auto-select current month or first available
      if (availableMonths.length > 0) {
        setSelectedMonth(availableMonths[0]);
      } else {
        // No emails yet, show current month
        const now = new Date();
        setSelectedMonth({
          year: now.getFullYear(),
          month: now.getMonth() + 1,
          label: now.toLocaleDateString('en-US', { year: 'numeric', month: 'long' }),
        });
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load available months');
    } finally {
      setLoading(false);
    }
  };

  const loadEmails = async (year: number, month: number) => {
    setRefreshing(true);
    setError(null);
    try {
      const res = await apiClient.getSentEmails(year, month);
      setEmails(res.emails || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load emails');
    } finally {
      setRefreshing(false);
    }
  };

  const loadEmailDetails = async (emailId: string) => {
    setLoadingEmail(true);
    try {
      const res = await apiClient.getSentEmail(
        emailId,
        selectedMonth?.year,
        selectedMonth?.month
      );
      setSelectedEmail(res.email);
    } catch (e: any) {
      console.error('Failed to load email details:', e);
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleEmailClick = (email: SentEmailSummary) => {
    loadEmailDetails(email.id);
  };

  const handleBackToList = () => {
    setSelectedEmail(null);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const [year, month] = value.split('-').map(Number);
    const monthOption = months.find(m => m.year === year && m.month === month);
    if (monthOption) {
      setSelectedMonth(monthOption);
      setSelectedEmail(null);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'graph_api_request':
        return 'Graph API Request';
      case 'notification':
        return 'Notification';
      case 'approval':
        return 'Approval';
      case 'workflow':
        return 'Workflow';
      default:
        return type;
    }
  };

  const getTypeBadgeVariant = (type: string): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'outline' => {
    switch (type) {
      case 'graph_api_request':
        return 'secondary';
      case 'approval':
        return 'warning';
      case 'workflow':
        return 'default';
      default:
        return 'outline';
    }
  };

  const filteredEmails = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return emails;
    return emails.filter((e) => {
      const subject = String(e.subject || '').toLowerCase();
      const to = (e.to || []).map((x) => String(x).toLowerCase()).join(' ');
      return subject.includes(q) || to.includes(q);
    });
  })();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent maxWidth="3xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {selectedEmail && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToList}
                  className="h-8 px-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
              )}
              <DialogTitle>
                {selectedEmail ? 'Email Details' : 'Sent Emails'}
              </DialogTitle>
            </div>
            {!selectedEmail && (
              <div className="flex items-center gap-2">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search subject or recipient…"
                  className="h-8 w-56"
                />
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <select
                    value={selectedMonth ? `${selectedMonth.year}-${selectedMonth.month}` : ''}
                    onChange={handleMonthChange}
                    className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-800"
                    disabled={loading}
                  >
                    {months.map((m) => (
                      <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
                        {m.label}
                      </option>
                    ))}
                    {months.length === 0 && selectedMonth && (
                      <option value={`${selectedMonth.year}-${selectedMonth.month}`}>
                        {selectedMonth.label}
                      </option>
                    )}
                  </select>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => selectedMonth && loadEmails(selectedMonth.year, selectedMonth.month)}
                  disabled={refreshing || loading}
                  className="h-8 px-2"
                >
                  <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} />
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>

        <DialogBody>
          {loading && (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-start gap-3 p-3">
                  <Skeleton className="w-8 h-8 rounded" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200">
              {error}
            </div>
          )}

          {/* Email List View */}
          {!loading && !selectedEmail && !error && (
            <>
              {filteredEmails.length === 0 ? (
                <div className="py-12 text-center">
                  <Mail className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    No Emails Sent
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No emails have been sent during {selectedMonth?.label || 'this period'}.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[50vh] overflow-y-auto">
                  {filteredEmails.map((email) => (
                    <button
                      key={email.id}
                      onClick={() => handleEmailClick(email)}
                      className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "w-8 h-8 rounded flex items-center justify-center flex-shrink-0",
                          email.success 
                            ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                            : "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400"
                        )}>
                          {email.success ? (
                            <CheckCircle2 className="w-4 h-4" />
                          ) : (
                            <XCircle className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              {email.subject}
                            </span>
                            <Badge variant={getTypeBadgeVariant(email.type)} className="flex-shrink-0">
                              {getTypeLabel(email.type)}
                            </Badge>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            To: {email.to.join(', ')}
                          </div>
                          <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            {formatTimestamp(email.sentAt)}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Email Detail View */}
          {selectedEmail && (
            <div className="space-y-4">
              {loadingEmail ? (
                <div className="space-y-3">
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-64 w-full" />
                </div>
              ) : (
                <>
                  {/* Status Badge */}
                  <div className="flex items-center gap-2">
                    {selectedEmail.success ? (
                      <Badge variant="success" className="flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Sent Successfully
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="flex items-center gap-1">
                        <XCircle className="w-3 h-3" />
                        Failed
                      </Badge>
                    )}
                    <Badge variant={getTypeBadgeVariant(selectedEmail.type)}>
                      {getTypeLabel(selectedEmail.type)}
                    </Badge>
                  </div>

                  {/* Email Metadata */}
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 space-y-2 text-sm">
                    <div className="flex">
                      <span className="font-medium w-24 text-gray-500 dark:text-gray-400">From:</span>
                      <span className="text-gray-900 dark:text-gray-100">{selectedEmail.from}</span>
                    </div>
                    <div className="flex">
                      <span className="font-medium w-24 text-gray-500 dark:text-gray-400">To:</span>
                      <span className="text-gray-900 dark:text-gray-100">{selectedEmail.to.join(', ')}</span>
                    </div>
                    <div className="flex">
                      <span className="font-medium w-24 text-gray-500 dark:text-gray-400">Subject:</span>
                      <span className="text-gray-900 dark:text-gray-100">{selectedEmail.subject}</span>
                    </div>
                    <div className="flex">
                      <span className="font-medium w-24 text-gray-500 dark:text-gray-400">Sent:</span>
                      <span className="text-gray-900 dark:text-gray-100">{formatTimestamp(selectedEmail.sentAt)}</span>
                    </div>
                    {selectedEmail.messageId && (
                      <div className="flex">
                        <span className="font-medium w-24 text-gray-500 dark:text-gray-400">Message ID:</span>
                        <span className="text-gray-900 dark:text-gray-100 font-mono text-xs break-all">
                          {selectedEmail.messageId}
                        </span>
                      </div>
                    )}
                    {selectedEmail.error && (
                      <div className="flex">
                        <span className="font-medium w-24 text-gray-500 dark:text-gray-400">Error:</span>
                        <span className="text-red-600 dark:text-red-400">{selectedEmail.error}</span>
                      </div>
                    )}
                    {selectedEmail.metadata && Object.keys(selectedEmail.metadata).length > 0 && (
                      <div className="pt-2 border-t border-gray-200 dark:border-gray-700 mt-2">
                        <span className="font-medium text-gray-500 dark:text-gray-400">Metadata:</span>
                        <pre className="mt-1 text-xs bg-gray-100 dark:bg-gray-900 p-2 rounded overflow-x-auto">
                          {JSON.stringify(selectedEmail.metadata, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>

                  {/* Email Content */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      Email Content
                    </div>
                    <div className="max-h-[40vh] overflow-y-auto">
                      {selectedEmail.html ? (
                        <iframe
                          srcDoc={selectedEmail.html}
                          className="w-full h-[40vh] border-0"
                          sandbox="allow-same-origin"
                          title="Email content"
                        />
                      ) : selectedEmail.text ? (
                        <pre className="p-4 text-sm whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                          {selectedEmail.text}
                        </pre>
                      ) : (
                        <div className="p-4 text-sm text-gray-500 dark:text-gray-400 italic">
                          No content available
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
